import { CommonModule, NgClass } from '@angular/common';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
  computed,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { CabinetService } from '@cabinet/cabinet.service';
import { FileModel } from '@core/models/file.model';
import { MessageModel } from '@core/models/message.model';
import { StatusModel } from '@core/models/status.model';
import { TagModel } from '@core/models/tag.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import * as MessageActions from '@core/redux/message/message.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { FileService } from '@core/services/file.service';
import { SocketService } from '@core/services/socket.service';
import { StoreTaskService } from '@core/services/store-task.service';
import { UnsavedChangesService } from '@core/services/unsaved-changes.service';
import { Store } from '@ngrx/store';
import { AvatarSelectComponent } from '@ux/components/avatar-select/avatar-select.component';
import { CalendarComponent } from '@ux/components/calendar/calendar.component';
import { EditorComponent } from '@ux/components/editor/editor.component';
import { PopupStatusComponent } from '@ux/components/select-popup/popup-status/popup-status.component';
import { StatusComponent } from '@ux/components/status/status.component';
import { AutoResizeDirective } from '@ux/directives/auto-resize.directive';
import * as _ from 'lodash';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, debounceTime } from 'rxjs/operators';
import * as visibility from 'visibilityjs';

import { TaskViewArchivePopupComponent } from '../task-view-archive-popup/task-view-archive-popup.component';
import { TaskViewCommentsComponent } from '../task-view-comments/task-view-comments.component';
import { TaskViewCommentsFormComponent } from '../task-view-comments-form/task-view-comments-form.component';
import { TaskViewFilesComponent } from '../task-view-files/task-view-files.component';
import { TaskViewFollowersComponent } from '../task-view-followers/task-view-followers.component';
import { TaskViewTagComponent } from '../task-view-tag/task-view-tag.component';

interface TaskViewState {
  files: any[];
  isOpenStatusPopup: boolean;
  isOpenCalendarPopup: boolean;
  openTaskId: string | null;
  isOpenTask: boolean;
  allowScrollDown: boolean;
  isOpenArchivePopup: boolean;
  isWindowFocus: boolean;
  editorData: any;
  isEditorFocused: boolean;
  isDirty: boolean;
  isOpenSavePopup: boolean;
}

@Component({
  selector: 'app-task-view',
  templateUrl: './task-view.component.html',
  styleUrls: ['./task-view.component.less'],
  imports: [
    NgClass,
    AvatarSelectComponent,
    TaskViewTagComponent,
    TaskViewCommentsComponent,
    StatusComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TaskViewArchivePopupComponent,
    PopupStatusComponent,
    CalendarComponent,
    TaskViewFilesComponent,
    TaskViewCommentsFormComponent,
    TaskViewFollowersComponent,
    NgClickOutsideDelayOutsideDirective,
    EditorComponent,
    AutoResizeDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewComponent implements AfterViewInit, OnDestroy {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly socketService = inject(SocketService);
  private readonly fileService = inject(FileService);
  private readonly route = inject(ActivatedRoute);
  private readonly taskService = inject(StoreTaskService);
  private readonly сabinetService: CabinetService = inject(CabinetService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('scroll') scroll: ElementRef;
  @ViewChild('scrollEnd') scrollEnd: ElementRef;
  @ViewChild('inputUploadFiles') inputUploadFiles: any;
  @ViewChild('editor') editor: EditorComponent;

  private readonly unsavedChangesService = inject(UnsavedChangesService);
  // Один общий промис на все параллельные вызовы guard'а
  private pendingNavPromise: Promise<boolean> | null = null;
  private pendingNavResolve: ((value: boolean) => void) | null = null;

  // Сигналы
  readonly state = signal<TaskViewState>({
    files: [],
    isOpenStatusPopup: false,
    isOpenCalendarPopup: false,
    openTaskId: null,
    isOpenTask: false,
    allowScrollDown: false,
    isOpenArchivePopup: false,
    isWindowFocus: true,
    editorData: {},
    isEditorFocused: false,
    isDirty: false,
    isOpenSavePopup: false,
  });

  readonly task = signal<TaskModel>(new TaskModel());
  readonly nameControl = new FormControl('');
  readonly newMessageSubject$: Subject<MessageModel> = new Subject();
  readonly oldTask: { name: string; body: any } = { name: '', body: {} };

  // Computed свойства
  readonly files = computed(() => this.state().files);
  readonly isOpenStatusPopup = computed(() => this.state().isOpenStatusPopup);
  readonly isOpenCalendarPopup = computed(() => this.state().isOpenCalendarPopup);
  readonly openTaskId = computed(() => this.state().openTaskId);
  readonly isOpenTask = computed(() => this.state().isOpenTask);
  readonly allowScrollDown = computed(() => this.state().allowScrollDown);
  readonly editorData = computed(() => this.state().editorData);
  readonly isOpenArchivePopup = computed(() => this.state().isOpenArchivePopup);
  readonly isEditorFocused = computed(() => this.state().isEditorFocused);
  readonly isDirty = computed(() => this.state().isDirty);
  readonly isOpenSavePopup = computed(() => this.state().isOpenSavePopup);

  // Методы обновления состояния
  private updateState(newState: Partial<TaskViewState>) {
    this.state.update(state => ({ ...state, ...newState }));
  }

  constructor() {
    // подписка на открытую задачу
    this.taskService
      .getOpenTask()
      .pipe(
        filter(task => !!task),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(task => {
        const updatedTask = new TaskModel().setAttributes(task);
        this.task.set(updatedTask);
        this.oldTask.name = task.name;
        this.oldTask.body = task.body;
        this.nameControl.setValue(task.name, { emitEvent: false });

        // Проверяем, изменился ли ID задачи
        const isTaskIdChanged = task.id !== this.openTaskId();

        // Обновляем данные редактора только если:
        // 1. Изменился ID задачи (переключение на другую задачу)
        // 2. ИЛИ редактор не в фокусе и нет несохраненных изменений
        if (isTaskIdChanged) {
          this.updateState({ editorData: task.body, isDirty: false });
        } else if (!this.isEditorFocused() && !this.isDirty()) {
          this.updateState({ editorData: task.body });
        }
      });

    // Подписка на изменение имени задачи
    this.nameControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value !== null) {
        const currentTask = this.task();
        const updatedTask = new TaskModel().setAttributes({
          ...currentTask,
          name: value,
        });
        this.task.set(updatedTask);
      }
    });

    // Подписка на изменение параметров URL
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, queryParams]) => params.get('id') || queryParams.get('id')),
        filter(id => !!id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => {
        if (id !== this.openTaskId()) {
          this._toggleTask(id);
        }
      });

    this.newMessageSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(newMessage => {
      this.scrollToBottom(true);
    });

    // Регистрируем обработчик в сервисе для CanDeactivate guard'а.
    // Angular запускает canDeactivate-гарды параллельно (mergeMap), поэтому
    // все параллельные вызовы должны разделять один и тот же промис —
    // иначе второй гард не дождётся ответа пользователя и навигация отменится.
    const handler = (): Promise<boolean> | boolean => {
      if (!this.isDirty()) {
        return true;
      }
      if (this.pendingNavPromise) {
        return this.pendingNavPromise;
      }
      this.pendingNavPromise = new Promise<boolean>(resolve => {
        this.pendingNavResolve = resolve;
        this.updateState({ isOpenSavePopup: true });
      });
      return this.pendingNavPromise;
    };
    this.unsavedChangesService.register(handler);
    this.destroyRef.onDestroy(() => {
      this.unsavedChangesService.unregister(handler);
      if (this.pendingNavResolve) {
        this.pendingNavResolve(true);
        this.pendingNavResolve = null;
        this.pendingNavPromise = null;
      }
    });
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files.length) {
      this.onFileUpload(event.dataTransfer.files);
    }
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: any): void {
    this.updateState({ isWindowFocus: true });
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: any): void {
    this.updateState({ isWindowFocus: false });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isDirty()) {
      event.preventDefault();
    }
  }

  onOpenStatusPopup(event) {
    event.stopPropagation();
    event.preventDefault();
    this.updateState({ isOpenStatusPopup: true });
  }

  onCloseStatusPopup() {
    this.updateState({ isOpenStatusPopup: false });
  }

  onOpenCalendarPopup(event) {
    event.stopPropagation();
    event.preventDefault();
    this.updateState({ isOpenCalendarPopup: true });
  }

  onCloseCalendarPopup() {
    this.updateState({ isOpenCalendarPopup: false });
  }

  onCloseArchivePopup() {
    this.updateState({ isOpenArchivePopup: false });
  }

  ngAfterViewInit() {
    this.scroll.nativeElement.scrollIntoView(false);
  }

  ngOnDestroy() {
    this.socketService.closeTask();
    this.store.dispatch(new TaskActions.CloseAction());
  }

  // Обработчик изменения данных редактора
  onEditorDataChanged(data: any) {
    // Обновляем состояние с новыми данными редактора и помечаем как несохраненные
    this.updateState({ editorData: data, isDirty: true });

    // Обновляем модель задачи с новыми данными
    const currentTask = this.task();
    const updatedTask = new TaskModel().setAttributes({
      ...currentTask,
      body: data,
    });
    this.task.set(updatedTask);
  }

  onSaveTask() {
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
    this.updateState({ isDirty: false });
  }

  onChangeResponsible(responsibleId: string) {
    const updatedTask = new TaskModel().setAttributes(this.task());
    updatedTask.responsibleId = responsibleId;
    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
  }

  onChangeStatus(status: StatusModel) {
    const updatedTask = new TaskModel().setAttributes(this.task());
    updatedTask.statusId = status.id;
    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
    this.onCloseStatusPopup();
  }

  onChangeTags(tag: TagModel) {
    const updatedTask = new TaskModel().setAttributes(this.task());

    // Check if tag already exists in the tags array
    const tagIndex = updatedTask.tags.indexOf(tag.id);

    if (tagIndex > -1) {
      // If tag exists, remove it
      updatedTask.tags = updatedTask.tags.filter(id => id !== tag.id);
    } else {
      // If tag doesn't exist, add it
      updatedTask.tags = [...updatedTask.tags, tag.id];
    }

    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.SaveAction(updatedTask));
  }

  onChangeDeadline(date) {
    const updatedTask = new TaskModel().setAttributes(this.task());
    updatedTask.deadlineAt = date.mDate.toISOString();
    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
    this.onCloseCalendarPopup();
  }

  onClearDeadline() {
    const updatedTask = new TaskModel().setAttributes(this.task());
    updatedTask.deadlineAt = '';
    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
    this.onCloseCalendarPopup();
  }

  onFileUpload({ files }: any) {
    const currentFiles = [...this.state().files];
    for (let i = 0; files.length > i; i++) {
      currentFiles.push(files[i]);
    }
    this.updateState({ files: currentFiles });

    this.inputUploadFiles.nativeElement.value = '';
    currentFiles.forEach((file, i) => {
      file.progress = 0;
      file.isError = false;
      file.error = null;
      this.fileService
        .upload(file, 'task', this.task().id, this.task().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          event => {
            file.index = i;
            if (event.type === HttpEventType.UploadProgress) {
              file.progress = Math.round((100 * event.loaded) / event.total);
            } else if (event instanceof HttpResponse) {
              file.progress = 100;
              const currentTask = this.task();
              const updatedTask = new TaskModel().setAttributes({
                ...currentTask,
                files: [...currentTask.files, event.body],
              });
              this.task.set(updatedTask);
              const index = _.findIndex(currentFiles, { name: file.name });
              const newFiles = [...currentFiles];
              newFiles.splice(index, 1);
              this.updateState({ files: newFiles });
            }
          },
          error => {
            file.error = error.error.file;
            file.isError = true;
          }
        );
    });
  }

  onComplete() {
    if (!this.task().isComplete) {
      const currentTask = this.task();
      const updatedTask = new TaskModel().setAttributes({
        ...currentTask,
        isComplete: !currentTask.isComplete,
      });
      this.task.set(updatedTask);
      this.store.dispatch(new TaskActions.SaveAction(updatedTask));
    }
    this.updateState({ isOpenArchivePopup: true });
  }

  onFavorite() {
    const currentTask = this.task();
    const updatedTask = new TaskModel().setAttributes({
      ...currentTask,
      favorites: [...currentTask.favorites],
    });
    updatedTask.isFavorite = !currentTask.isFavorite;
    this.task.set(updatedTask);
    this.store.dispatch(new TaskActions.FavoriteAction(updatedTask));
  }

  onUpdateFollowers(followers: string[]) {
    const updatedTask = new TaskModel().setAttributes({
      ...this.task(),
      followers,
    });

    this.task.set(updatedTask);
    this.onSave();
  }

  onSave() {
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
  }

  onSaveName() {
    if (this.nameControl.value !== this.oldTask.name) {
      this.onSave();
    }
  }

  onClose() {
    // Guard перехватит навигацию и покажет попап, если есть несохранённые изменения
    if (this.сabinetService.layout === 'board') {
      this.router.navigate([{ outlets: { task: null } }], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate([], {
        queryParams: { id: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  onConfirmSavePopup() {
    this.store.dispatch(new TaskActions.SaveAction(this.task()));
    this.updateState({ isDirty: false, isOpenSavePopup: false });
    // Разрешаем переход всем параллельным guard'ам через общий промис
    if (this.pendingNavResolve) {
      this.pendingNavResolve(true);
      this.pendingNavResolve = null;
      this.pendingNavPromise = null;
    }
  }

  onCancelSavePopup() {
    this.updateState({ isOpenSavePopup: false });
    // Отменяем переход - guard вернёт false
    if (this.pendingNavResolve) {
      this.pendingNavResolve(false);
      this.pendingNavResolve = null;
      this.pendingNavPromise = null;
    }
  }

  canScroll(): void {
    const nativeElement = this.scroll.nativeElement;
    const scrollTop = Math.ceil(nativeElement.scrollTop);
    const scrollHeight = nativeElement.scrollHeight;
    const clientHeight = nativeElement.clientHeight;
    const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 1;

    this.updateState({
      allowScrollDown: !atBottom && !visibility.hidden() && this.state().isWindowFocus,
    });
  }

  onScroll(): void {
    this.canScroll();

    if (this.allowScrollDown() && this.task().countNotifications) {
      const currentTask = this.task();
      const updatedTask = new TaskModel().setAttributes({
        ...currentTask,
        countNotifications: 0,
      });
      this.task.set(updatedTask);
      this.store.dispatch(new TaskActions.ClearNotificationsAction(this.task()));
    }
  }

  private _toggleTask(id: string | null) {
    this.updateState({
      openTaskId: id,
      isOpenTask: !!id,
      isEditorFocused: false, // Сбрасываем фокус при смене задачи
    });

    if (id) {
      this.store.dispatch(new TaskActions.OpenAction(id));
      this.store.dispatch(new MessageActions.SearchAction(id));
      this.socketService.openTask(id);
    }
  }

  public scrollToBottom(scrollByNewMessage: boolean = false) {
    this.canScroll();
    if ((scrollByNewMessage || this.allowScrollDown()) && this.state().isWindowFocus) {
      try {
        setTimeout(() => {
          this.scrollEnd.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      } catch (err) {
        console.log(err);
      }
    }
  }

  // Добавляем методы для отслеживания фокуса редактора
  onEditorFocus() {
    this.updateState({ isEditorFocused: true });
  }

  onEditorBlur() {
    this.updateState({ isEditorFocused: false });
  }

  // Метод для загрузки изображений из редактора
  handleImageUpload = (file: File): Promise<{ success: boolean; file: { url: string } }> =>
    new Promise((resolve, reject) => {
      if (!this.task().id) {
        reject({ success: false, message: 'Задача не сохранена' });
        return;
      }

      this.fileService
        .uploadFromEditor(file, 'task', this.task().id, this.task().id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          event => {
            if (event.type === HttpEventType.UploadProgress) {
              // Отображение прогресса загрузки можно добавить при необходимости
            } else if (event instanceof HttpResponse) {
              // Файл успешно загружен, ответ уже в формате EditorJS
              const response = event.body as { success: boolean; file: { url: string; id: string } };

              // Добавляем файл в список файлов задачи, если есть полная информация о файле
              if (response.file && response.file.id) {
                const currentTask = this.task();
                const updatedTask = new TaskModel().setAttributes({
                  ...currentTask,
                  files: [
                    ...currentTask.files,
                    {
                      id: response.file.id,
                      url: response.file.url,
                      name: file.name,
                      size: file.size,
                      type: file.type,
                    },
                  ],
                });
                this.task.set(updatedTask);
              }

              // Возвращаем ответ для EditorJS
              resolve(response);
            }
          },
          error => {
            console.error('Ошибка при загрузке изображения:', error);
            reject({
              success: false,
              message: error.error?.message || 'Ошибка при загрузке изображения',
            });
          }
        );
    });

  onDeleteFile(file: FileModel) {
    // Сначала удаляем файл из модели задачи
    const currentTask = this.task();
    const updatedTask = new TaskModel().setAttributes({
      ...currentTask,
      files: currentTask.files.filter(f => f.id !== file.id),
    });

    // Вызываем сервис для удаления файла
    this.fileService
      .delete(file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // После успешного удаления обновляем только состояние в Redux
          this.task.set(updatedTask);
          this.store.dispatch(new TaskActions.SaveSuccessAction(updatedTask));
        },
        error: error => {
          console.error('Ошибка при удалении файла:', error);
        },
      });
  }
}
